import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, X, CheckCircle, Clock, ChefHat, Bike, Search, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import api from '../utils/api';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

const CART_EXPIRY_HOURS = 2;

const getCartKey = (tableNumber) => `cart_table_${tableNumber}`;
const getOrdersKey = (tableNumber) => `orders_table_${tableNumber}`;

const saveCart = (tableNumber, cart) => {
  try {
    localStorage.setItem(getCartKey(tableNumber), JSON.stringify({ cart, savedAt: Date.now() }));
  } catch {}
};

const loadCart = (tableNumber) => {
  try {
    const raw = localStorage.getItem(getCartKey(tableNumber));
    if (!raw) return [];
    const { cart, savedAt } = JSON.parse(raw);
    const hoursOld = (Date.now() - savedAt) / (1000 * 60 * 60);
    if (hoursOld > CART_EXPIRY_HOURS) { localStorage.removeItem(getCartKey(tableNumber)); return []; }
    return cart || [];
  } catch { return []; }
};

const saveOrders = (tableNumber, orders) => {
  try {
    localStorage.setItem(getOrdersKey(tableNumber), JSON.stringify({ orders, savedAt: Date.now() }));
  } catch {}
};

const loadOrders = (tableNumber) => {
  try {
    const raw = localStorage.getItem(getOrdersKey(tableNumber));
    if (!raw) return [];
    const { orders, savedAt } = JSON.parse(raw);
    const hoursOld = (Date.now() - savedAt) / (1000 * 60 * 60);
    if (hoursOld > CART_EXPIRY_HOURS) { localStorage.removeItem(getOrdersKey(tableNumber)); return []; }
    return orders || [];
  } catch { return []; }
};

const clearTableStorage = (tableNumber) => {
  try {
    localStorage.removeItem(getCartKey(tableNumber));
    localStorage.removeItem(getOrdersKey(tableNumber));
  } catch {}
};

const STATUS_STEPS = [
  { key: 'pending', label: 'Order Received', icon: Clock, color: 'text-orange-500', bg: 'bg-orange-100', border: 'border-orange-300', desc: 'Your order has been received and is waiting to be confirmed.' },
  { key: 'preparing', label: 'Being Prepared', icon: ChefHat, color: 'text-blue-500', bg: 'bg-blue-100', border: 'border-blue-300', desc: 'The kitchen is preparing your order right now!' },
  { key: 'ready', label: 'Ready', icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', border: 'border-green-300', desc: 'Your order is ready! It will be served to your table shortly.' },
  { key: 'delivered', label: 'Delivered', icon: Bike, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-300', desc: 'Your order has been delivered. Enjoy your meal!' },
  { key: 'cancelled', label: 'Cancelled', icon: X, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-300', desc: 'This order has been cancelled.' },
];

const OrderTracker = ({ orders, onOrderAgain, onCancelOrder }) => {
  const getStep = (status) => STATUS_STEPS.findIndex((s) => s.key === status);

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-10">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Your Orders</h1>
            <p className="text-sm text-gray-500">Live status updates</p>
          </div>
          <Button onClick={onOrderAgain} size="sm" className="bg-[#E65100] hover:bg-[#D84800] text-white">
            <Plus className="w-4 h-4 mr-1" /> Order More
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {orders.map((order) => {
          const currentStep = getStep(order.status);
          const stepInfo = STATUS_STEPS[currentStep] || STATUS_STEPS[0];
          const Icon = stepInfo.icon;

          return (
            <div key={order.id} className={`bg-white rounded-2xl border-2 ${stepInfo.border} overflow-hidden shadow-sm`}>
              <div className={`${stepInfo.bg} px-5 py-4 flex items-center gap-3`}>
                <div className={`p-2 bg-white rounded-full ${stepInfo.color}`}><Icon className="w-5 h-5" /></div>
                <div>
                  <p className={`font-bold text-base ${stepInfo.color}`}>{stepInfo.label}</p>
                  <p className="text-sm text-gray-600">{stepInfo.desc}</p>
                </div>
              </div>

              <div className="px-5 pt-4">
                <div className="flex items-center gap-0">
                  {STATUS_STEPS.map((step, idx) => {
                    const done = idx <= currentStep;
                    const StepIcon = step.icon;
                    return (
                      <React.Fragment key={step.key}>
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${done ? `${stepInfo.bg} ${stepInfo.color}` : 'bg-gray-100 text-gray-400'}`}>
                            <StepIcon className="w-4 h-4" />
                          </div>
                          <span className={`text-xs mt-1 font-medium ${done ? stepInfo.color : 'text-gray-400'}`}>{step.label}</span>
                        </div>
                        {idx < STATUS_STEPS.length - 1 && (
                          <div className={`flex-1 h-1 mb-5 transition-all duration-500 ${idx < currentStep ? stepInfo.bg : 'bg-gray-200'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {order.status === 'pending' && (
                <div className="px-5 pb-3">
                  <button onClick={() => onCancelOrder(order.id)} className="w-full py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition">
                    Cancel Order
                  </button>
                </div>
              )}

              <div className="px-5 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Items Ordered</p>
                <div className="space-y-1">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-800">{item.name} × {item.quantity}</span>
                      <span className="text-gray-600">₹{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {order.customer_comment && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    <span className="font-semibold">Your note: </span>{order.customer_comment}
                  </div>
                )}
                <div className="border-t border-gray-100 mt-3 pt-3 flex justify-between font-bold text-gray-900">
                  <span>Total</span>
                  <span className="text-[#E65100]">₹{order.subtotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomerMenu = () => {
  const { restaurantId, tableNumber } = useParams();
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState(() => loadCart(tableNumber));
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [placedOrders, setPlacedOrders] = useState(() => loadOrders(tableNumber));
  const [comment, setComment] = useState('');
  const [showTracker, setShowTracker] = useState(() => loadOrders(tableNumber).length > 0);
  const [vegOnly, setVegOnly] = useState(false);
  // Modifier picker state
  const [modifierItem, setModifierItem] = useState(null);   // item whose modifiers are being picked
  const [modifierSelections, setModifierSelections] = useState({});  // { groupTitle: [chosenNames] }
  const socketRef = useRef(null);
  const placedOrdersRef = useRef([]);
  const setShowTrackerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { saveCart(tableNumber, cart); }, [cart, tableNumber]);
  useEffect(() => { saveOrders(tableNumber, placedOrders); }, [placedOrders, tableNumber]);

  placedOrdersRef.current = placedOrders;
  setShowTrackerRef.current = setShowTracker;

  useEffect(() => {
    fetchData();

    const joinRoom = (sock) => sock.emit('join_room', { room: 'customer', restaurant_id: restaurantId });

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => { joinRoom(socket); });
    socket.on('reconnect', () => { joinRoom(socket); });

    socket.on('order_updated', (updatedOrder) => {
      const currentOrders = placedOrdersRef.current;
      const oldOrder = currentOrders.find(o => o.id === updatedOrder.id);
      if (!oldOrder || oldOrder.status === updatedOrder.status) return;

      const updated = currentOrders.map((o) => o.id === updatedOrder.id ? { ...o, status: updatedOrder.status } : o);
      saveOrders(tableNumber, updated);
      setPlacedOrders(updated);

      const allDone = updated.every(o => o.status === 'delivered' || o.status === 'cancelled');
      if (allDone) setTimeout(() => clearTableStorage(tableNumber), 5000);

      if (updatedOrder.status === 'preparing') {
        toast('👨‍🍳 Kitchen is preparing your order!', { description: 'Your food is being prepared right now.', duration: 5000 });
        setShowTrackerRef.current(true);
      } else if (updatedOrder.status === 'ready') {
        toast('✅ Your order is ready!', { description: 'Your food will be served to your table shortly.', duration: 6000 });
        setShowTrackerRef.current(true);
      } else if (updatedOrder.status === 'delivered') {
        toast('🍽️ Enjoy your meal!', { description: 'Your order has been delivered.', duration: 5000 });
        setShowTrackerRef.current(true);
      } else if (updatedOrder.status === 'cancelled') {
        toast.error('Order cancelled', { description: 'Your order has been cancelled by the kitchen.', duration: 5000 });
      }
    });

    socketRef.current = socket;
    return () => { socket.disconnect(); };
  }, []);

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await api.cancelOrderByCustomer(orderId, restaurantId);
      toast.success('Order cancelled');
      setPlacedOrders((prev) => {
        const updated = prev.map((o) => (o.id === orderId ? { ...o, status: 'cancelled' } : o));
        saveOrders(tableNumber, updated);
        return updated;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot cancel — order is already being prepared');
    }
  };

  const fetchData = async () => {
    try {
      const [menuRes, catRes] = await Promise.all([api.getMenu(restaurantId), api.getCategories(restaurantId)]);
      setMenu(menuRes.data);
      setCategories(['All', ...catRes.data.map((c) => c.name)]);
    } catch {
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  // ── Filtering: search + category + veg ──
  const filteredMenu = menu
    .filter((item) => selectedCategory === 'All' || item.category === selectedCategory)
    .filter((item) => !vegOnly || item.is_veg !== false)
    .filter((item) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
    });

  // When user types in search, reset category to All so results aren't doubly filtered
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.trim()) setSelectedCategory('All');
  };

  const clearSearch = () => {
    setSearchQuery('');
    searchRef.current?.focus();
  };

  const addToCart = (item) => {
    // If item has modifiers, open the picker first
    if (item.modifiers && item.modifiers.length > 0) {
      // Pre-select the first option of each required single-select group
      const defaults = {};
      item.modifiers.forEach(g => {
        if (g.required && !g.multi_select && g.options.length > 0) {
          defaults[g.title] = [g.options[0].name];
        } else {
          defaults[g.title] = [];
        }
      });
      setModifierSelections(defaults);
      setModifierItem(item);
      return;
    }
    // No modifiers — add directly
    const existing = cart.find((c) => c.menu_item_id === item.id);
    if (existing) {
      setCart(cart.map((c) => c.menu_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { menu_item_id: item.id, name: item.name, price: item.price, quantity: 1, modifiers: [] }]);
    }
  };

  const confirmModifiers = () => {
    const item = modifierItem;
    if (!item) return;
    // Validate required groups
    for (const g of item.modifiers) {
      if (g.required && (!modifierSelections[g.title] || modifierSelections[g.title].length === 0)) {
        toast.error(`Please select an option for "${g.title}"`);
        return;
      }
    }
    // Calculate extra price from selected options
    let extraPrice = 0;
    item.modifiers.forEach(g => {
      (modifierSelections[g.title] || []).forEach(chosen => {
        const opt = g.options.find(o => o.name === chosen);
        if (opt) extraPrice += opt.extra_price || 0;
      });
    });
    // Build modifiers array for order
    const selectedMods = item.modifiers
      .map(g => ({ group_title: g.title, chosen: modifierSelections[g.title] || [] }))
      .filter(m => m.chosen.length > 0);

    const totalPrice = item.price + extraPrice;
    // Each modifier combination is a unique cart entry (keyed by item id + mod hash)
    const cartKey = item.id + JSON.stringify(modifierSelections);
    const existing = cart.find(c => c._cartKey === cartKey);
    if (existing) {
      setCart(cart.map(c => c._cartKey === cartKey ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { menu_item_id: item.id, name: item.name, price: totalPrice, quantity: 1, modifiers: selectedMods, _cartKey: cartKey }]);
    }
    setModifierItem(null);
    setModifierSelections({});
  };

  const toggleModifierOption = (groupTitle, optionName, multiSelect) => {
    setModifierSelections(prev => {
      const current = prev[groupTitle] || [];
      if (multiSelect) {
        return { ...prev, [groupTitle]: current.includes(optionName) ? current.filter(n => n !== optionName) : [...current, optionName] };
      } else {
        return { ...prev, [groupTitle]: [optionName] };
      }
    });
  };

  const updateQuantity = (itemId, delta) => {
    setCart(
      cart.map((c) => c.menu_item_id === itemId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c)
        .filter((c) => c.quantity > 0)
    );
  };

  const removeFromCart = (itemId) => setCart(cart.filter((c) => c.menu_item_id !== itemId));

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const placeOrder = async () => {
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }
    try {
      // Strip internal _cartKey field before sending to server
      const cleanItems = cart.map(({ _cartKey, ...item }) => item);
      const res = await api.createOrder({ table_number: parseInt(tableNumber), items: cleanItems, subtotal, customer_comment: comment.trim() || null }, restaurantId);
      toast.success('Order placed!');
      setPlacedOrders((prev) => {
        const updated = [res.data, ...prev];
        saveOrders(tableNumber, updated);
        return updated;
      });
      setCart([]);
      saveCart(tableNumber, []);
      setComment('');
      setShowCart(false);
      setShowTracker(true);
    } catch {
      toast.error('Failed to place order');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading menu...</div>
      </div>
    );
  }

  if (showTracker && placedOrders.length > 0) {
    return <OrderTracker orders={placedOrders} onOrderAgain={() => setShowTracker(false)} onCancelOrder={cancelOrder} />;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-24">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Our Menu</h1>
              <p className="text-sm text-gray-600">Table {tableNumber}</p>
            </div>
            <div className="flex items-center gap-2">
              {placedOrders.length > 0 && (
                <button
                  onClick={() => setShowTracker(true)}
                  className="relative px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-full text-sm font-medium hover:bg-green-100 transition flex items-center gap-1"
                >
                  <CheckCircle className="w-4 h-4" />
                  Track Order
                </button>
              )}
              <button
                onClick={() => setVegOnly(!vegOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border-2 transition ${vegOnly ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-green-500 text-green-700'}`}
                title="Show only vegetarian items"
              >
                <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${vegOnly ? 'border-white' : 'border-green-600'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${vegOnly ? 'bg-white' : 'bg-green-600'}`} />
                </span>
                Veg
              </button>
              <button
                onClick={() => setShowCart(true)}
                className="relative p-3 bg-[#E65100] text-white rounded-full hover:bg-[#D84800]"
                data-testid="open-cart-button"
              >
                <ShoppingCart className="w-6 h-6" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cart.reduce((sum, item) => sum + item.quantity, 0)}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Search Bar ── */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search dishes, e.g. paneer, biryani..."
              className="w-full pl-9 pr-9 py-2.5 bg-gray-100 border border-transparent rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100] focus:bg-white transition"
            />
            {searchQuery && (
              <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Category Tabs — hidden while searching ── */}
      {!searchQuery && (
        <div className="bg-white border-b border-gray-200 sticky top-[132px] z-10">
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition ${
                    selectedCategory === cat
                      ? 'bg-[#E65100] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}-button`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Menu Items ── */}
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Search result count */}
        {searchQuery && (
          <p className="text-sm text-gray-500 mb-4">
            {filteredMenu.length === 0
              ? `No results for "${searchQuery}"`
              : `${filteredMenu.length} result${filteredMenu.length !== 1 ? 's' : ''} for "${searchQuery}"`}
          </p>
        )}

        <div className="space-y-4">
          {filteredMenu.length === 0 && !searchQuery ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium">No items in this category</p>
            </div>
          ) : (
            filteredMenu.map((item) => (
              <div
                key={item.id}
                className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition ${item.is_available === false ? 'border-red-100 opacity-70' : 'border-gray-100'}`}
                data-testid={`menu-item-${item.id}`}
              >
                <div className="flex gap-4 p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${item.is_veg !== false ? 'border-green-600' : 'border-red-600'}`}>
                        <span className={`w-2 h-2 rounded-full ${item.is_veg !== false ? 'bg-green-600' : 'bg-red-600'}`} />
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                      {item.is_available === false && (
                        <span className="text-xs font-bold px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Out of Stock</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xl font-bold text-[#E65100]">₹{item.price.toFixed(2)}</span>
                      {item.is_available === false ? (
                        <span className="text-xs text-gray-400 font-medium border border-gray-200 rounded-full px-3 py-1.5">Unavailable</span>
                      ) : (() => {
                        const cartItem = cart.find((c) => c.menu_item_id === item.id);
                        return cartItem ? (
                          <div className="flex items-center gap-2 bg-orange-50 border border-[#E65100] rounded-full px-2 py-1">
                            <button onClick={() => updateQuantity(item.id, -1)} className="w-7 h-7 rounded-full bg-[#E65100] text-white flex items-center justify-center hover:bg-[#D84800] transition">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-6 text-center font-bold text-[#E65100] text-sm">{cartItem.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="w-7 h-7 rounded-full bg-[#E65100] text-white flex items-center justify-center hover:bg-[#D84800] transition">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            onClick={() => addToCart(item)}
                            size="sm"
                            className="bg-[#E65100] hover:bg-[#D84800] text-white h-9 rounded-full px-4"
                            data-testid={`add-to-cart-${item.id}-button`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                  <img src={item.image_url} alt={item.name} className={`w-24 h-24 object-cover rounded-lg ${item.is_available === false ? 'grayscale' : ''}`} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Modifier Picker Modal ── */}
      {modifierItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setModifierItem(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{modifierItem.name}</h3>
                <p className="text-sm text-[#E65100] font-semibold">₹{modifierItem.price.toFixed(2)} base price</p>
              </div>
              <button onClick={() => setModifierItem(null)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {modifierItem.modifiers.map((group) => (
                <div key={group.title}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-gray-900 text-sm">{group.title}</p>
                    {group.required && <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">Required</span>}
                    {group.multi_select && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Pick multiple</span>}
                  </div>
                  <div className="space-y-2">
                    {group.options.map((opt) => {
                      const chosen = (modifierSelections[group.title] || []).includes(opt.name);
                      return (
                        <button
                          key={opt.name}
                          type="button"
                          onClick={() => toggleModifierOption(group.title, opt.name, group.multi_select)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition ${chosen ? 'border-[#E65100] bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${chosen ? 'border-[#E65100] bg-[#E65100]' : 'border-gray-300'}`}>
                              {chosen && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{opt.name}</span>
                          </div>
                          {opt.extra_price > 0 && (
                            <span className="text-sm font-semibold text-[#E65100]">+₹{opt.extra_price.toFixed(2)}</span>
                          )}
                          {opt.extra_price === 0 && <span className="text-xs text-gray-400">Free</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
              <Button onClick={confirmModifiers} className="w-full h-12 bg-[#E65100] hover:bg-[#D84800] text-white font-semibold text-base">
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cart Drawer ── */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Your Order</h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-cart-button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              {cart.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Your cart is empty</p>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {cart.map((item) => (
                      <div key={item.menu_item_id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-[#E65100] font-semibold">₹{item.price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateQuantity(item.menu_item_id, -1)} className="p-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-testid={`decrease-quantity-${item.menu_item_id}-button`}>
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.menu_item_id, 1)} className="p-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-testid={`increase-quantity-${item.menu_item_id}-button`}>
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button onClick={() => removeFromCart(item.menu_item_id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md" data-testid={`remove-from-cart-${item.menu_item_id}-button`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg font-semibold text-gray-900">Subtotal</span>
                      <span className="text-2xl font-bold text-[#E65100]" data-testid="cart-subtotal">₹{subtotal.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 text-center">Taxes will be added at final billing</p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Add a comment (optional)</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="e.g. Less spicy, no onions, extra sauce..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100] resize-none"
                      rows={2}
                    />
                  </div>
                  <Button onClick={placeOrder} className="w-full h-12 bg-[#E65100] hover:bg-[#D84800] text-white text-lg font-semibold" data-testid="place-order-button">
                    Place Order
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerMenu;

